import React, { useMemo } from 'react';

import { CustomerItemDto } from '@/api/generated';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { CustomTable, CustomTableColumn } from '@/components/table/CustomTable.comp.tsx';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { TableBasicProps } from '@/models/TableBasicProps.model.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CustomersHeaderAdd } from '@/views/customers/list/components/header/components/CustomersHeaderAdd.comp.tsx';
import { CustomersTableRow } from '@/views/customers/list/components/table/components/CustomersTableRow.comp.tsx';
import { CustomerSearchFormModel } from '@/views/customers/list/model/CustomersSearchForm.model.ts';
import {
  CustomersTableState,
  initialCustomersTableState,
} from '@/views/customers/list/model/CustomersTableState.model.ts';
import { useCustomersFormValidation } from '@/views/customers/list/utils/useCustomersForm.validation.ts';
import { useGetCustomers } from '@/views/customers/list/utils/useGetCustomers.util.ts';

type Props = TableBasicProps<CustomerItemDto, CustomerSearchFormModel> & {
  urlStateEnabled?: boolean;
};

export const CustomersTable: React.FC<Props> = props => {
  const {
    additionalButtons,
    customRow,
    hidePaper,
    onCustomAction,
    pageSize,
    tableKey,
    tableSx,
    urlStateEnabled,
  } = props;
  const { t } = useTranslationWithPrefix('views.customers.table');
  const { getCustomers, isLoading } = useGetCustomers();
  const validation = useCustomersFormValidation();
  const { USERS_SHORT } = useDictionariesContext();
  const { userSelectOption } = useUserContext();
  const { customerStatusOptions } = useGetSelectOptions();

  const filters: CustomTableFilterDefinition<CustomerSearchFormModel>[] = useMemo(
    () => [
      {
        label: t('head.fullTextSearchInput'),
        name: 'fullTextSearchInput',
        type: CustomTableFilterTypeEnum.Text,
        xs: 6,
      },
      {
        label: t('head.ownerIdOfCustomerContact'),
        name: 'ownerIdOfCustomerContact',
        options: USERS_SHORT,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
      {
        isMulti: true,
        label: t('head.statuses'),
        name: 'statuses',
        options: customerStatusOptions,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
    ],
    [USERS_SHORT, customerStatusOptions, t],
  );

  const tableHead: CustomTableColumn<string>[] = useMemo(() => {
    const head = [
      {
        id: 'name',
        label: t('body.name'),
        sx: { width: '24%' },
      },
      {
        id: 'nip',
        label: t('body.nip'),
        sx: { width: '10%' },
      },
      {
        id: 'address',
        label: t('body.address'),
        sx: { width: '20%' },
      },
      {
        id: 'type',
        label: t('body.type'),
        sx: { width: '10%' },
      },
      {
        id: 'flag',
        label: t('body.flag'),
        sx: { width: '15%' },
      },
      {
        id: 'status',
        label: t('body.status'),
        sx: { width: '8%' },
      },
    ];
    if (onCustomAction) {
      head.push({
        id: 'actions',
        label: '',
        sx: { width: '6%' },
      });
    }
    return head;
  }, [onCustomAction, t]);

  const init = async (initialParams: CustomersTableState) => {
    return await getCustomers(initialParams);
  };

  return (
    <CustomTable<string, CustomerSearchFormModel, CustomerItemDto>
      additionalButtons={[
        { button: CustomersHeaderAdd, name: 'add' },
        ...(additionalButtons ?? []),
      ]}
      CustomTableRow={customRow ?? CustomersTableRow}
      customTableRowAction={onCustomAction}
      filters={filters}
      getTableDataRequest={getCustomers}
      hidePaper={hidePaper}
      idKey="customerId"
      init={init}
      initParams={{
        ...initialCustomersTableState,
        pageSize: pageSize ?? 10,
        params: {
          ...initialCustomersTableState.params,
          ownerIdOfCustomerContact: userSelectOption,
        },
      }}
      isLoading={isLoading}
      size="small"
      tableHead={tableHead}
      tableKey={tableKey}
      tableSx={tableSx}
      urlStateEnabled={urlStateEnabled}
      validation={validation}
    />
  );
};
